import 'core-js/proposals/reflect-metadata';
import { Container, interfaces } from 'inversify';
import { DI_TYPES } from './di-types';
import { PersistenceBackend } from './persistence';
import { BoundedEventStore } from './event-store';
import { HTTPTransport } from './http-transport';
import { timestampFactory, getTimezone, perfTimestampFactory } from './util/time';
import { V2ClientSideRequestProcessor } from './v2/v2request-processor';
import { dynamicModuleProvider, registerConstants } from './util/ioc';
import { NativeFunctionsProvider } from './util/iframe-natives';
//V2
import { V2ClientSDK } from './v2/v2clientsdk';
import { V2Transport } from '@jetfabric/schemas/lib/protocol-sdk-api-v2/protocol';
import { getWindowObject } from './util/window-provider';
import { V2ClientStateFactory } from './v2/client-state/root';
import { ClientEventDB } from './v2/client-event-db/client-event-db';
import { random } from './util/random';
import { FeatureStates, V2ClientFeatureState } from './v2/client-state/features';
import { DEFAULT_FEATURE_STATES } from './constants';
import {
  ClientEventDBProvider,
  ClientEventDBProviderCreator,
} from './util/client-event-db-provider';
import type { evaluate } from './util/conditional-expressions';

export const getContainer = () => {
  // V1
  const container = registerConstants(new Container());
  container
    .bind<PersistenceBackend>(DI_TYPES.PersistenceBackend)
    .toConstantValue(getWindowObject()?.localStorage!);
  container.bind<BoundedEventStore>(DI_TYPES.BoundedEventStore).to(BoundedEventStore);
  container.bind<typeof timestampFactory>(DI_TYPES.TimestampFactory).toFunction(timestampFactory);
  container
    .bind<typeof perfTimestampFactory>(DI_TYPES.PerfTimestampFactory)
    .toFunction(perfTimestampFactory);
  container
    .bind<NativeFunctionsProvider>(DI_TYPES.NativeFunctionsProvider)
    .to(NativeFunctionsProvider);

  // V2
  container.bind<V2Transport>(DI_TYPES.V2Transport).to(HTTPTransport);
  container.bind<V2ClientSDK>(DI_TYPES.V2ClientSDK).to(V2ClientSDK);
  container.bind<typeof getTimezone>(DI_TYPES.getTimezone).toFunction(getTimezone);
  container
    .bind<V2ClientSideRequestProcessor>(DI_TYPES.V2ClientSideRequestProcessor)
    .to(V2ClientSideRequestProcessor);
  container.bind<typeof getWindowObject>(DI_TYPES.WindowProvider).toFunction(getWindowObject);
  container.bind<V2ClientStateFactory>(DI_TYPES.V2ClientStateFactory).to(V2ClientStateFactory);
  container
    .bind<V2ClientFeatureState>(DI_TYPES.V2ClientFeatureState)
    .to(V2ClientFeatureState)
    .inSingletonScope();
  container.bind<ClientEventDB>(DI_TYPES.ClientEventDB).to(ClientEventDB).inSingletonScope();
  container
    .bind<ClientEventDBProvider>(DI_TYPES.ClientEventDBProvider)
    .toProvider(ClientEventDBProviderCreator);
  container.bind<typeof random>(DI_TYPES.random).toFunction(random);
  container
    .bind<FeatureStates>(DI_TYPES.DefaultFeatureStates)
    .toConstantValue(DEFAULT_FEATURE_STATES);
  container
    .bind<interfaces.Provider<typeof evaluate>>(DI_TYPES.ConditionaExpressionProvider)
    .toProvider<typeof evaluate>(
      dynamicModuleProvider(
        () =>
          import(
            /* webpackChunkName: "conditional-expressions" */ './util/conditional-expressions'
          ),
        m => m.evaluate
      )
    );
  return container;
};
